import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core'
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'
import { filter, finalize, first, switchMap, takeUntil, tap } from 'rxjs/operators'
import { Observable } from 'rxjs'

import { User } from '@shared/models/user.model'
import { RoleService } from '@shared/services/role.service'
import { Role } from '@shared/models/role.model'
import { dateFormats } from '@core/constants/dateFormats'
import { AuthService } from '@app/modules/auth/auth.service'
import { ConfirmUnsavedChanges } from '@core/guards/unsaved-changes.guard'
import { takeFormSnapshot } from '@core/helpers/take-form-snapshot'
import { handleFormErrors } from '@core/helpers/handle-form-errors'
import { ResellerService } from '@app/modules/admin/reseller/services/reseller.service'
import { ResellerUser } from '@shared/models/reseller.model'
import { DestroyService } from '@shared/services/destroy.service'
import { DialogService } from '@shared/dialogs/dialog.service'

@Component({
  selector: 'lin-reseller-user-form',
  templateUrl: './reseller-user-form.component.html',
  styles: [
    `
      .toolbar-button {
        margin-right: 15px;
      }
      .blocking-status {
        padding-top: 8px;
        min-height: 60px;
      }
    `,
  ],
  providers: [DestroyService],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ResellerUserFormComponent implements OnInit, ConfirmUnsavedChanges {
  @Input() resellerUser: ResellerUser

  userForm: FormGroup
  currentUserId: string
  formError: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errorMessages: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  selectedManager: any
  roles: Role[]
  hasAllTeamsPermissions: boolean
  isLoaded = false
  passwordResetting = false
  isSubmitting = false
  loggedUser: User

  private isFormChanged: ReturnType<typeof takeFormSnapshot>

  readonly dateFormats = dateFormats

  get resellerId(): string {
    return this.route.snapshot.params.resellerId as string
  }

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly roleService: RoleService,
    private readonly authService: AuthService,
    private readonly resellerService: ResellerService,
    private readonly dialog: DialogService,
    private readonly destroyService: DestroyService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.createForm()
    this.getData()
    this.setFormValue()
  }

  get isSSOUser(): boolean {
    return this.resellerUser.isSSO
  }

  getResellerRoles(): Observable<Role[]> {
    return this.roleService.resellerTeamRoles$.pipe(first())
  }

  getData(): void {
    this.authService.currentUser$
      .pipe(
        filter(Boolean),
        first(),
        tap((user) => (this.loggedUser = user)),
        switchMap(() => this.getResellerRoles()),
        tap((roles) => (this.roles = roles)),
        finalize(() => {
          this.isLoaded = true
          this.cdr.markForCheck()
        }),
        takeUntil(this.destroyService),
      )
      .subscribe()
  }

  hasUnsavedChanges(): boolean {
    return this.isFormChanged()
  }

  createForm(): void {
    this.userForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      name: ['', Validators.required],
      surname: ['', Validators.required],
      phoneNumber: ['', [Validators.required, Validators.minLength(8), this.patternValidator(/^\+(?:[\d]*)$/, { phoneNumber: true })]],
      role: ['', Validators.required],
      language: '',
    })

    this.userForm.valueChanges.pipe(takeUntil(this.destroyService)).subscribe(() => this.onFormValuesChanged())
  }

  resetPassword(): void {
    this.dialog
      .confirm('Do you want to reset password for this user?')
      .afterClosed()
      .pipe(
        filter(Boolean),
        tap(() => (this.passwordResetting = true)),
        switchMap(() => this.authService.requestNewPassword(this.resellerUser.email, true)),
        finalize(() => (this.passwordResetting = false)),
        takeUntil(this.destroyService),
      )
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        (error) => {
          this.formError = handleFormErrors(this.userForm)(error)
          this.cdr.markForCheck()
        },
      )
  }

  handleUpdateUser(): void {
    this.updateUser()
  }

  private setFormValue(): void {
    if (!this.resellerUser) return

    const { resellerUser } = this

    this.userForm.setValue({
      email: resellerUser.email,
      name: resellerUser.name,
      surname: resellerUser.surname,
      phoneNumber: resellerUser.phone,
      role: resellerUser.role?.id ?? '',
      language: resellerUser.language ?? '',
    })
    this.userForm.addControl('status', this.formBuilder.control({ value: resellerUser.status, disabled: true }))
    this.userForm.addControl('blockingStatus', this.formBuilder.control(resellerUser.isBlocked))
    this.userForm.addControl('blockingMessage', this.formBuilder.control(resellerUser.blockingMessage))

    this.isFormChanged = takeFormSnapshot(this.userForm)
  }

  private onFormValuesChanged(): void {
    this.formError = ''
  }

  private updateUserRequest(): Observable<ResellerUser> {
    this.isSubmitting = true
    const saveData = this.prepareDataForSaving()
    const resellerId = this.loggedUser.team.resellerId || this.resellerId
    const userId = this.resellerUser ? this.resellerUser.id : null
    const action$ = userId
      ? this.resellerService.editResellerUser(resellerId, userId, saveData)
      : this.resellerService.addResellerUser(resellerId, saveData)

    return action$.pipe(
      tap(() => {
        this.isFormChanged = takeFormSnapshot(this.userForm)
        void this.router.navigate(userId ? ['../../../'] : ['../'], { relativeTo: this.route })
      }),
      finalize(() => {
        this.isSubmitting = false
        this.cdr.markForCheck()
      }),
    )
  }

  private updateUser(): void {
    this.updateUserRequest()
      .pipe(takeUntil(this.destroyService))
      .subscribe(
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        () => {},
        (error) => {
          this.formError = handleFormErrors(this.userForm)(error)
          this.cdr.markForCheck()
        },
      )
  }

  private prepareDataForSaving(): Partial<ResellerUser> {
    const userData = this.userForm.value as Partial<ResellerUser> & { phoneNumber: string; blockingStatus: boolean; userRole: string }
    const saveData = {
      email: userData.email,
      name: userData.name,
      surname: userData.surname,
      phone: userData.phoneNumber,
      roleId: userData.role,
    }

    if (this.resellerUser) {
      saveData['isBlocked'] = userData.blockingStatus
      saveData['blockingMessage'] = userData.blockingMessage
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return,@typescript-eslint/no-explicit-any
    return saveData as any
  }

  private patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value) {
        return null
      }

      // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
      return regex.test(control.value) ? null : error
    }
  }
}
